import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { actionPlanKeys, fetchActionPlanSearchV2 } from '@/core/api/queries';
import ObligationService from '@/shared/services/ObligationService';
import {
  collectProposalIdsFromNotifications,
  resolveProposalMetaFromSearchRows,
} from './obligationProposalNotificationUtils';

/**
 * Za OBLIGATION_PROPOSAL_UPDATED (related_entity_id = proposal_id):
 * search-one → obligation_id, zatim proposal/search za tab/focus meta i naziv obaveze.
 *
 * @param {{ enabled?: boolean, notifications?: object[] }} [options]
 * @param {boolean} [options.enabled] - Da li je hook aktivan.
 * @param {object[]} [options.notifications] - Lista obaveštenja za resolve proposal meta.
 * @returns {{ proposalById: object, obligations: object[], draftActionPlanId: string|null, isLoading: boolean }}
 */
export function useNotificationObligationProposals({
  enabled = false,
  notifications = [],
} = {}) {
  const proposalIds = useMemo(
    () => collectProposalIdsFromNotifications(notifications),
    [notifications],
  );
  const idsKey = proposalIds.join(',');
  const [proposalById, setProposalById] = useState({});
  const [obligations, setObligations] = useState([]);
  const [draftActionPlanId, setDraftActionPlanId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!enabled || !proposalIds.length) {
      setProposalById({});
      setObligations([]);
      setDraftActionPlanId(null);
      setIsLoading(false);
      return undefined;
    }

    let cancelled = false;
    setProposalById({});
    setObligations([]);
    setDraftActionPlanId(null);
    setIsLoading(true);

    (async () => {
      try {
        const apData = await queryClient.fetchQuery({
          queryKey: actionPlanKeys.searchV2(),
          queryFn: fetchActionPlanSearchV2,
        });
        const draftAp = apData?.draft ?? null;
        const draftApId = draftAp?.id != null ? String(draftAp.id) : null;
        if (!cancelled) setDraftActionPlanId(draftApId);

        const updates = {};
        const obligationMetaById = new Map();
        const obligationIds = new Set();

        await Promise.all(
          proposalIds.map(async (proposalId) => {
            try {
              const resolved = await ObligationService.searchOneProposal(proposalId);
              if (!resolved?.obligation_id) return;
              obligationIds.add(resolved.obligation_id);
              updates[proposalId] = {
                proposal_id: String(proposalId),
                obligation_id: resolved.obligation_id,
                action_plan_id: draftApId,
                obligation_question_id: null,
                fieldKey: null,
                formTab: null,
              };
            } catch {
              /* proposal may be unavailable */
            }
          }),
        );

        const proposalsByObligation = new Map();
        await Promise.all(
          [...obligationIds].map(async (obligationId) => {
            try {
              const searchRes = await ObligationService.searchProposalResponses({
                obligation_id: obligationId,
              });
              proposalsByObligation.set(
                obligationId,
                searchRes?.obligation_proposals || [],
              );
            } catch {
              proposalsByObligation.set(obligationId, []);
            }
          }),
        );

        Object.keys(updates).forEach((proposalId) => {
          const entry = updates[proposalId];
          const rows = proposalsByObligation.get(entry.obligation_id) || [];
          const meta = resolveProposalMetaFromSearchRows(rows, proposalId);
          updates[proposalId] = {
            ...entry,
            ...meta,
          };
        });

        if (draftApId) {
          await Promise.all(
            [...obligationIds].map(async (obligationId) => {
              try {
                const oblRes = await ObligationService.search({
                  action_plan_id: draftApId,
                  id: obligationId,
                });
                const obl = (oblRes?.obligations ?? []).find(
                  (row) => String(row?.id) === String(obligationId),
                );
                if (!obl) return;
                obligationMetaById.set(String(obligationId), {
                  ...obl,
                  _apId: draftApId,
                  _apStatus: draftAp?.status ?? 'DRAFT',
                });
              } catch {
                /* AP may be unavailable for role */
              }
            }),
          );
        }

        if (!cancelled) {
          setProposalById(updates);
          setObligations([...obligationMetaById.values()]);
        }
      } catch {
        if (!cancelled) {
          setProposalById({});
          setObligations([]);
          setDraftActionPlanId(null);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, idsKey, proposalIds, queryClient]);

  return {
    proposalById,
    obligations,
    draftActionPlanId,
    isLoading,
  };
}
